
import React from 'react';
import { Project } from '@/hooks/useProjects';
import { useProjectRrwebSessions } from '@/hooks/useProjectRrwebSessions';
import { useRrwebSessions } from '@/hooks/useRrwebSessions';
import { SimpleResponsiveRrwebPlayer } from '@/components/playback/SimpleResponsiveRrwebPlayer';
import VideoExportPanel from '@/components/VideoExportPanel';

interface VideoPreviewProps {
  project: Project;
}

const VideoPreview = ({ project }: VideoPreviewProps) => {
  const { data: projectSessions } = useProjectRrwebSessions(project.id);
  const { data: allSessions } = useRrwebSessions();
  const [currentSessionIndex, setCurrentSessionIndex] = React.useState(0);

  // Get the actual session data for linked sessions
  const linkedSessions = React.useMemo(() => {
    if (!allSessions || !projectSessions) return [];
    return allSessions.filter(session => 
      projectSessions.some(ps => ps.rrweb_session_id === session.id)
    ).sort((a, b) => {
      const aOrder = projectSessions.find(ps => ps.rrweb_session_id === a.id)?.order_in_project || 0;
      const bOrder = projectSessions.find(ps => ps.rrweb_session_id === b.id)?.order_in_project || 0;
      return aOrder - bOrder;
    });
  }, [allSessions, projectSessions]);

  const currentSession = linkedSessions[currentSessionIndex];

  const handleNextSession = () => {
    if (currentSessionIndex < linkedSessions.length - 1) {
      setCurrentSessionIndex(currentSessionIndex + 1);
    }
  };

  const handlePrevSession = () => {
    if (currentSessionIndex > 0) {
      setCurrentSessionIndex(currentSessionIndex - 1);
    }
  };

  return (
    <div className="flex-1 bg-editor-medium border-r border-editor-light p-6 flex flex-col min-h-0">
      <div className="bg-black rounded-lg overflow-hidden relative flex-1 min-h-0">
        {/* Session Navigation */}
        {linkedSessions.length > 1 && (
          <div className="absolute top-4 left-4 z-10 bg-black/50 rounded-lg p-2 text-white text-sm">
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrevSession}
                disabled={currentSessionIndex === 0}
                className="px-2 py-1 bg-editor-accent disabled:bg-gray-600 disabled:cursor-not-allowed rounded text-xs"
              >
                ← Prev
              </button>
              <span>
                {currentSessionIndex + 1} / {linkedSessions.length}
              </span>
              <button
                onClick={handleNextSession}
                disabled={currentSessionIndex === linkedSessions.length - 1}
                className="px-2 py-1 bg-editor-accent disabled:bg-gray-600 disabled:cursor-not-allowed rounded text-xs"
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* Video Preview Area */}
        {currentSession?.events_storage_path ? (
          <SimpleResponsiveRrwebPlayer
            sessionEventsStoragePath={currentSession.events_storage_path}
            autoPlay={false}
            className="absolute inset-0 w-full h-full"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-4">📹</div>
              <h3 className="text-xl font-semibold text-white mb-2">{project.title}</h3>
              <p className="text-gray-400 mb-4">
                {linkedSessions.length === 0 
                  ? 'No sessions linked to this project' 
                  : 'Session preview will appear here'
                }
              </p>
              {project.description && (
                <p className="text-sm text-gray-500 mb-4">{project.description}</p>
              )}
            </div>
          </div>
        )}

        {/* Project Stats */}
        <div className="absolute top-4 right-4 bg-black/50 rounded-lg p-3 text-white text-sm">
          <div>Sessions: {linkedSessions.length || 0}</div>
          <div>Resolution: {project.output_resolution_height || 1080}p</div>
          <div>Aspect: {project.aspect_ratio || '16:9'}</div>
          {currentSession && (
            <div className="mt-1 pt-1 border-t border-gray-600">
              <div className="text-xs text-gray-300">
                Current: {currentSession.name || `Session ${currentSessionIndex + 1}`}
              </div>
            </div>
          )}
        </div>

        {/* Export Status */}
        {project.export_status && project.export_status !== 'none' && (
          <div className="absolute bottom-4 right-4 bg-black/50 rounded-lg p-3 text-white text-sm">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                project.export_status === 'completed' ? 'bg-green-500' :
                project.export_status === 'processing' ? 'bg-yellow-500' :
                project.export_status === 'queued' ? 'bg-blue-500' :
                'bg-red-500'
              }`} />
              <span className="capitalize">{project.export_status}</span>
            </div>
          </div>
        )}
      </div>

      {/* Export Panel */}
      {linkedSessions.length > 0 && (
        <div className="mt-4 p-4 bg-editor-light rounded-lg flex-shrink-0">
          <h4 className="text-sm font-medium text-white mb-3">Video Export</h4>
          <VideoExportPanel project={project} />
        </div>
      )}
    </div>
  );
};

export default VideoPreview;
